import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Link,
  Button,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import en_ja from "../../translations/en_ja";

const WarrantyDetails = ({
  open,
  onClose,
  warrantyData = null,
  onApprove,
  onReject,
  actionLoading = false,
}) => {

  const formatDate = (d) => {
    if (!d) return en_ja["-"] ?? "-";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return en_ja["-"] ?? "-";

    return `${String(dt.getDate()).padStart(2, "0")}/${String(
      dt.getMonth() + 1
    ).padStart(2, "0")}/${dt.getFullYear()}`;
  };

  const formatDOB = (dob) => {
    if (!dob) return "-";

    // backend sends YYYYMMDD
    if (/^\d{8}$/.test(dob)) {
      const year = dob.slice(0, 4);
      const month = dob.slice(4, 6);
      const day = dob.slice(6, 8);

      return `${day}/${month}/${year}`;
    }

    // fallback if already normal date
    return formatDate(dob);
  };

  const cleanUrl = (u) => {
    if (!u) return "";
    return String(u).replace(/"%22/g, "").replace(/%22/g, "").replace(/"/g, "");
  };

  const getReceiptFile = () => {
    const raw = warrantyData?.raw || {};

    const url =
      warrantyData?.purchase_receipt_url ||
      warrantyData?.purchase_receipt ||
      raw?.purchase_receipt ||
      raw?.purchase_receipt_url ||
      raw?.receipt_url ||
      "";

    return cleanUrl(url) || "";
  };

  const getReceiptName = () => {
    const url = getReceiptFile();
    if (!url) return en_ja["No file uploaded"] ?? "-";

    try {
      const parts = String(url).split("/");
      return decodeURIComponent(parts[parts.length - 1] || url);
    } catch {
      const parts = String(url).split("/");
      return parts[parts.length - 1] || url || "receipt";
    }
  };

  const renderProofFile = () => {
    const fileUrl = getReceiptFile();
    const fileName = getReceiptName();

    if (!fileUrl) {
      return (
        <Box
          sx={{
            borderRadius: 1,
            background: "#fafafa",
            p: 2,
            border: "1px solid #eee",
          }}
        >
          <Typography variant="body2">-</Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          borderRadius: 1,
          background: "#fafafa",
          p: 2,
          border: "1px solid #eee",
        }}
      >
        <Link
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
        >
          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
            {fileName}
          </Typography>
        </Link>
      </Box>
    );
  };

  const status = (warrantyData?.status || "").toString();
  const statusLower = status.toLowerCase();

  const statusBg =
    statusLower.includes("approved")
      ? "#198754"
      : statusLower.includes("rejected")
        ? "#dc3545"
        : statusLower.includes("pending")
          ? "#fd7e14"
          : "#6c757d";

  const isAlreadyApproved = statusLower.includes("approved");
  const isAlreadyRejected = statusLower.includes("rejected");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* HEADER */}
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            {en_ja["Warranty Registration Details"]}
          </Typography>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* USER INFO */}
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {en_ja["User Information"]}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: 6,
            rowGap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 240 }}>
            <Typography variant="caption" color="text.secondary">
              {en_ja["Name"]}:
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {warrantyData?.user_name ?? "-"}
            </Typography>

            <Box mt={1}>
              <Typography variant="caption" color="text.secondary">
                {en_ja["Phone"]}:
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {warrantyData?.phone ?? "-"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ minWidth: 240 }}>
            <Typography variant="caption" color="text.secondary">
              {en_ja["Email"]}:
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {warrantyData?.email ?? "-"}
            </Typography>

            <Box mt={1}>
              <Typography variant="caption" color="text.secondary">
                {en_ja["Date of Birth"]}:
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {formatDOB(warrantyData?.birth_date) ?? "-"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ minWidth: 240 }}>
            <Typography variant="caption" color="text.secondary">
              {en_ja["Gender"]}:
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {warrantyData?.gender ?? "-"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              {en_ja["PIN"]}:
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {warrantyData?.pin ?? "-"}
            </Typography>
          </Box>
        </Box>

        {/* PRODUCT INFO */}
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {en_ja["Product Information"]}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: 6,
            rowGap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 300 }}>
            <Typography variant="caption" color="text.secondary">
              {en_ja["Product name"]}:
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {warrantyData?.product_name ?? "-"}
            </Typography>
          </Box>

          <Box sx={{ minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary">
              {en_ja["Product code"]}:
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {warrantyData?.product_code ?? "-"}
            </Typography>
          </Box>
        </Box>

        {/* PURCHASE INFO */}
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {en_ja["Purchase Information"]}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: 6,
            rowGap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 240 }}>
            <Typography variant="caption" color="text.secondary">
              {en_ja["Purchase Date"]}:
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {formatDate(warrantyData?.purchase_date)}
            </Typography>

            <Box mt={1}>
              <Typography variant="caption">
                {en_ja["Purchase Store"]}:
              </Typography>
              <Typography fontWeight={600}>
                {warrantyData?.purchase_store ?? "-"}
              </Typography>
            </Box>

            <Box mt={1}>
              <Typography variant="caption" color="text.secondary">
                {en_ja["Date Registered"]}:
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {formatDate(warrantyData?.date_registered)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ minWidth: 240 }}>
            <Typography variant="caption" color="text.secondary">
              {en_ja["Purchase Cost"]}:
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {warrantyData?.product_price ?? "-"}
            </Typography>

            <Box
              sx={{
                mt: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {en_ja["Status"]}:
              </Typography>

              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1.6,
                  py: "4px",
                  borderRadius: "12px",
                  backgroundColor: statusBg,
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  textTransform: "capitalize",
                }}
              >
                {status || en_ja["Pending"] || "Pending"}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* PROOF */}
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {en_ja["Proof of Purchase"]}
        </Typography>

        {renderProofFile()}

        <Box sx={{ height: 8 }} />
      </DialogContent>

      {/* ACTIONS */}
      <DialogActions sx={{ p: 3 }}>
        <Button
          variant="outlined"
          onClick={() => onReject && onReject()}
          disabled={actionLoading || isAlreadyRejected}
          sx={{
            borderColor: "#dc3545",
            color: "#dc3545",
            textTransform: "none",
            "&:hover": {
              borderColor: "#c82333",
              backgroundColor: "rgba(220,53,69,0.04)",
            },
          }}
        >
          {actionLoading
            ? "..."
            : isAlreadyRejected
              ? en_ja["Rejected"]
              : en_ja["Reject Warranty"]}
        </Button>

        <Button
          variant="contained"
          onClick={() => onApprove && onApprove()}
          disabled={actionLoading || isAlreadyApproved}
          sx={{
            backgroundColor: isAlreadyApproved ? "#bcbcbc" : "rgba(220, 53, 69, 1)",
            color: "#fff",
            textTransform: "none",
            "&:hover": {
              backgroundColor: isAlreadyApproved ? "#bcbcbc" : "#c82333",
            },
          }}
        >
          {actionLoading
            ? "..."
            : isAlreadyApproved
              ? en_ja["Approved"]
              : en_ja["Approve Warranty"]}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WarrantyDetails;
